import { Component, computed, DestroyRef, inject, input, output, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { FileDropComponent } from '@app/shared/components/files/file-drop/file-drop.component';
import { FilePreviewListComponent } from '@app/shared/components/files/file-preview-list/file-preview-list.component';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { TaskCommentsDataService } from '@app/shared/services/task-comments-data.service';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs';

@Component({
  selector: 'proffeo-task-timeline-comment',
  templateUrl: './timeline-comment.component.html',
  host: {
    class: 'block w-full min-w-0'
  },
  imports: [
    FileDropComponent,
    FilePreviewListComponent,
    TranslatePipe,
    UiButtonComponent,
    UiSpinnerDirective,
    UiTextareaComponent
  ]
})
export class TaskTimelineCommentComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly taskCommentsService = inject(TaskCommentsDataService);
  private readonly deviceService = inject(DeviceService);

  protected readonly shouldAutofocus: Signal<boolean> = computed(() => !this.deviceService.isMobile());

  protected readonly files: WritableSignal<File[]> = signal<File[]>([]);
  protected readonly isSubmitting = signal(false);

  protected readonly commentText = signal<string | null>('');

  public readonly task = input.required<Task>();
  public readonly commentSent = output<void>();

  protected onRemoveFile(file: File): void {
    const remainingFiles = this.files().filter((value: File) => value !== file);
    this.files.set(remainingFiles);
  }

  protected onFilesAdded(files: File[]): void {
    const allFiles = [...this.files(), ...files];
    this.files.set(allFiles);
  }

  protected onSendComment(): void {
    if (this.isSubmitting()) {
      return;
    }

    const commentText = this.commentText() ?? '';
    const filesToUpload = this.files();

    this.isSubmitting.set(true);

    this.taskCommentsService
      .addCommentToTask(this.task().id, commentText, filesToUpload)
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.commentText.set('');
        this.files.set([]);
        this.commentSent.emit();
      });
  }
}
